import React, { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AssessmentIcon from "@mui/icons-material/Assessment";
import {
  formatUsd,
  formatUsdNoDecimals,
  formatSignedNumber,
  computeTradeBlotterSummary,
  emptyTradeSummaryMetrics,
  TRADE_SUMMARY_FIXED_ORDER_TYPES,
} from "./utility";

const ALL_FUNDS = "All";
const ALL_STRATEGIES = "All";

const headerColumnRule = (theme) =>
  theme.palette.mode === "dark"
    ? "0.1px solid rgba(255,255,255,0.9)"
    : "0.1px solid #ccc3c3";

const SUB_HEADER_COLUMN_RULE = "0.1px solid #ccc3c3";

const W = { orderType: 120, data: 80, dollars: 100 };

const border1 = (theme) => `1px solid ${theme.palette.divider}`;

const headerText = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black;

const subHeaderRowBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[200];

/** 9 columns: trades×3, shares×3, dollars×3 — Market/Crossing/Total each */
const SUB_HEADERS = [
  ["Market", "trades"],
  ["Crossing", "trades"],
  ["Total", "trades"],
  ["Market", "shares"],
  ["Crossing", "shares"],
  ["Total", "shares"],
  ["Market", "dollars"],
  ["Crossing", "dollars"],
  ["Total", "dollars"],
];

const sectionHeaderCell = (label, theme) => (
  <TableCell
    key={label}
    align="center"
    colSpan={3}
    sx={{
      fontWeight: 700,
      borderTop: "none",
      borderBottom: border1(theme),
      borderLeft: headerColumnRule(theme),
      borderRight: headerColumnRule(theme),
      py: 1,
      bgcolor: theme.palette.lightGrey.tableHeader,
      color: headerText(theme),
    }}
  >
    {label}
  </TableCell>
);

const subHeaderCell = (label, section, theme) => (
  <TableCell
    key={`${section}-${label}`}
    align="center"
    sx={{
      fontWeight: 600,
      borderBottom: border1(theme),
      borderLeft: SUB_HEADER_COLUMN_RULE,
      borderRight: SUB_HEADER_COLUMN_RULE,
      py: 0.8,
      borderTop: "none",
      bgcolor: subHeaderRowBg(theme),
      color: `${theme.palette.common.black} !important`,
      width: section === "dollars" ? W.dollars : W.data,
    }}
  >
    {label}
  </TableCell>
);

const dataCell = (content, section, theme, bold) => (
  <TableCell
    align="center"
    sx={{
      py: 0.9,
      color: theme.palette.text.primary,
      width: section === "dollars" ? W.dollars : W.data,
      ...(bold && { fontWeight: 600 }),
    }}
  >
    {content}
  </TableCell>
);

const metricCells = (row, theme) => {
  const cells = [
    [formatSignedNumber(row.noOfTradesMarket), "trades", false],
    [formatSignedNumber(row.noOfTradesCrossing), "trades", false],
    [formatSignedNumber(row.noOfTradesTotal), "trades", true],
    [formatUsdNoDecimals(row.sharesMarket), "shares", false],
    [formatUsdNoDecimals(row.sharesCrossing), "shares", false],
    [formatUsdNoDecimals(row.sharesTotal), "shares", true],
    [formatUsd(row.dollarsMarket), "dollars", false],
    [formatUsd(row.dollarsCrossing), "dollars", false],
    [formatUsd(row.dollarsTotal), "dollars", true],
  ];
  return cells.map(([content, section, bold], i) => (
    <React.Fragment key={`m-${i}`}>{dataCell(content, section, theme, bold)}</React.Fragment>
  ));
};

const filterCaptionSx = {
  display: "block",
  mb: 0.5,
  color: "text.secondary",
  fontWeight: 600,
  letterSpacing: 0.6,
  lineHeight: 1.2,
};

const SummaryDialog = ({ open, onClose, tradesData, livePrices, funds }) => {
  const theme = useTheme();
  const [selectedFund, setSelectedFund] = useState(ALL_FUNDS);
  const [selectedStrategy, setSelectedStrategy] = useState(ALL_STRATEGIES);
  const [expandedOrderType, setExpandedOrderType] = useState(null);

  const collectStrategyBreakdown =
    selectedFund !== ALL_FUNDS && selectedStrategy === ALL_STRATEGIES;

  useEffect(() => {
    if (open) {
      setSelectedFund(ALL_FUNDS);
      setSelectedStrategy(ALL_STRATEGIES);
      setExpandedOrderType(null);
    }
  }, [open]);

  useEffect(() => {
    if (selectedFund === ALL_FUNDS) {
      setSelectedStrategy(ALL_STRATEGIES);
    }
    setExpandedOrderType(null);
  }, [selectedFund]);

  useEffect(() => {
    setExpandedOrderType(null);
  }, [selectedStrategy]);

  const fundOptions = useMemo(() => {
    const set = new Set();
    (tradesData || []).forEach((t) => {
      if (t.fund && t.fund !== "N/A") set.add(t.fund);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tradesData]);

  const fundAbbreviationByName = useMemo(() => {
    const map = new Map();
    (funds || []).forEach((f) => {
      if (f?.name) map.set(f.name, f.abbreviation || "");
    });
    return map;
  }, [funds]);

  const displayFundLabel = (fundName) => {
    const fromFunds = fundAbbreviationByName.get(fundName);
    if (fromFunds) return fromFunds;
    const row = (tradesData || []).find((t) => t.fund === fundName);
    if (row?.fundAbbreviation) return row.fundAbbreviation;
    return fundName;
  };

  const selectOutlineSx = {
    borderRadius: 1,
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
  };

  const strategyOptions = useMemo(() => {
    if (selectedFund === ALL_FUNDS) return [];
    const set = new Set();
    (tradesData || []).forEach((t) => {
      if (t.fund === selectedFund && t.strategy && t.strategy !== "N/A") {
        set.add(t.strategy);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tradesData, selectedFund]);

  const tradesSubset = useMemo(() => {
    let t = tradesData || [];
    if (selectedFund !== ALL_FUNDS) {
      t = t.filter((x) => x.fund === selectedFund);
    }
    if (selectedStrategy !== ALL_STRATEGIES) {
      t = t.filter((x) => x.strategy === selectedStrategy);
    }
    return t;
  }, [tradesData, selectedFund, selectedStrategy]);

  const summaryData = useMemo(
    () => computeTradeBlotterSummary(tradesSubset, livePrices, collectStrategyBreakdown),
    [tradesSubset, livePrices, collectStrategyBreakdown]
  );

  const getStrategySubRows = (orderType) => {
    const fromData = new Map(
      (summaryData.strategyBreakdownByOrderType?.[orderType] || []).map((r) => [r.strategy, r])
    );
    return strategyOptions.map((name) => {
      const m = fromData.get(name);
      return { strategy: name, ...(m || emptyTradeSummaryMetrics()) };
    });
  };

  const totalRowHighlight =
    theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[200];

  const mainHeaderRowBg =
    theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100];

  const subRowBg =
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : theme.palette.grey[50];

  const cardBoxSx = {
    p: 2,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    backgroundColor: theme.palette.background.paper,
  };

  const statCards = [
    {
      label: "Total No Of Strategies",
      node: summaryData.totalStrategies,
      valueSx: { color: theme.palette.primary.main },
    },
    {
      label: "Filled Amount in Long Position",
      node: formatUsdNoDecimals(summaryData.filledAmountLong),
      valueSx: { color: theme.palette.success.main },
    },
    {
      label: "Filled Amount in Short Position",
      node: formatUsdNoDecimals(summaryData.filledAmountShort),
      valueSx: { color: theme.palette.warning.main },
    },
    {
      label: "Total Amount",
      node: formatUsdNoDecimals(summaryData.totalFilledAmount),
      valueSx: { color: theme.palette.info.main },
    },
  ];

  const shBg = subHeaderRowBg(theme);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: "18px",
          fontWeight: 600,
        }}
      >
        <AssessmentIcon sx={{ color: "primary.main" }} />
        Trades Summary
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 3 }}>
            {statCards.map(({ label, node, valueSx }) => (
              <Box key={label} sx={cardBoxSx}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "1.6rem", ...valueSx }}>
                  {node}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 2,
              mb: 1,
              width: { xs: "100%", sm: "55%" },
              maxWidth: 640,
            }}
          >
            <Box sx={{ minWidth: 120, maxWidth: 220, flex: "0 0 auto" }}>
              <Typography
                component="label"
                htmlFor="summary-dialog-fund-select"
                variant="caption"
                sx={filterCaptionSx}
              >
                FUNDS
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  id="summary-dialog-fund-select"
                  value={selectedFund}
                  onChange={(e) => setSelectedFund(e.target.value)}
                  sx={selectOutlineSx}
                  inputProps={{ "aria-label": "Filter by fund" }}
                >
                  <MenuItem value={ALL_FUNDS}>All</MenuItem>
                  {fundOptions.map((f) => (
                    <MenuItem key={f} value={f}>
                      {displayFundLabel(f)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ minWidth: 200, flex: "1 1 0", maxWidth: 250 }}>
              <Typography
                component="label"
                htmlFor="summary-dialog-strategy-select"
                variant="caption"
                sx={filterCaptionSx}
              >
                STRATEGIES
              </Typography>
              <FormControl size="small" fullWidth disabled={selectedFund === ALL_FUNDS}>
                <Select
                  id="summary-dialog-strategy-select"
                  value={selectedStrategy}
                  onChange={(e) => setSelectedStrategy(e.target.value)}
                  sx={selectOutlineSx}
                  inputProps={{ "aria-label": "Filter by strategy" }}
                >
                  <MenuItem value={ALL_STRATEGIES}>All Strategies</MenuItem>
                  {strategyOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              overflow: "hidden",
              "& thead .MuiTableCell-root": { fontSize: "0.82rem" },
              "& tbody .MuiTableCell-root": {
                borderColor: theme.palette.divider,
                fontSize: "0.82rem",
              },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[500],
                  }}
                >
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: "#ffffff",
                      letterSpacing: 0.3,
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      borderBottom: border1(theme),
                      py: 0.9,
                    }}
                  >
                    SUMMARY
                  </TableCell>
                </TableRow>
                <TableRow sx={{ bgcolor: mainHeaderRowBg }}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      borderTop: "none",
                      borderBottom: border1(theme),
                      borderLeft: headerColumnRule(theme),
                      borderRight: headerColumnRule(theme),
                      py: 0.9,
                      width: W.orderType,
                      bgcolor: theme.palette.lightGrey.tableHeader,
                      color: headerText(theme),
                    }}
                  >
                    Type
                  </TableCell>
                  {sectionHeaderCell("Trade Count", theme)}
                  {sectionHeaderCell("Dollars", theme)}
                  {sectionHeaderCell("Filled", theme)}
                </TableRow>
                <TableRow sx={{ bgcolor: shBg }}>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      borderBottom: border1(theme),
                      borderLeft: SUB_HEADER_COLUMN_RULE,
                      borderRight: SUB_HEADER_COLUMN_RULE,
                      py: 0.8,
                      borderTop: "none",
                      color: `${theme.palette.common.black} !important`,
                      bgcolor: shBg,
                    }}
                  />
                  {SUB_HEADERS.map(([label, section]) => subHeaderCell(label, section, theme))}
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryData.ordersByType.map((row) => {
                  const expandable =
                    collectStrategyBreakdown &&
                    TRADE_SUMMARY_FIXED_ORDER_TYPES.includes(row.orderType) &&
                    strategyOptions.length > 0;
                  const expanded = expandedOrderType === row.orderType;

                  return (
                    <React.Fragment key={row.orderType}>
                      <TableRow
                        hover
                        onClick={() => {
                          if (!expandable) return;
                          setExpandedOrderType((prev) =>
                            prev === row.orderType ? null : row.orderType
                          );
                        }}
                        sx={{
                          "&:hover": { bgcolor: theme.palette.action.hover },
                          bgcolor: row.orderType === "Total" ? totalRowHighlight : "inherit",
                          cursor: expandable ? "pointer" : "default",
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, py: 0.9, width: W.orderType }}>
                          {row.orderType}
                        </TableCell>
                        {metricCells(row, theme)}
                      </TableRow>
                      {expandable &&
                        expanded &&
                        getStrategySubRows(row.orderType).map((sub) => (
                          <TableRow
                            key={`${row.orderType}-${sub.strategy}`}
                            sx={{ bgcolor: subRowBg }}
                          >
                            <TableCell
                              sx={{
                                pl: 4,
                                py: 0.65,
                                fontSize: "0.8rem",
                                fontWeight: 500,
                                color: "text.secondary",
                              }}
                            >
                              {sub.strategy}
                            </TableCell>
                            {metricCells(sub, theme)}
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: 1,
        }}
      />
    </Dialog>
  );
};

export default SummaryDialog;
